import React from 'react';
import { Shield } from 'lucide-react';

export const PERMISSIONS = {
    // Admin Management
    MANAGE_ADMINS: 'manage_admins',
    VIEW_ADMINS: 'view_admins',

    // Temple Management
    MANAGE_TEMPLES: 'manage_temples',
    VIEW_TEMPLES: 'view_temples',
    APPROVE_TEMPLES: 'approve_temples',

    // User Management
    MANAGE_USERS: 'manage_users',
    VIEW_USERS: 'view_users',

    // Donation Management
    MANAGE_DONATIONS: 'manage_donations',
    VIEW_DONATIONS: 'view_donations',

    // Other Features
    MANAGE_POOJAS: 'manage_poojas',
    VIEW_POOJAS: 'view_poojas',

    MANAGE_REPORTS: 'manage_reports',

    MANAGE_SETTINGS: 'manage_settings',
} as const;

export const DEFAULT_ADMIN_PERMISSIONS = [
    PERMISSIONS.VIEW_TEMPLES,
    PERMISSIONS.VIEW_USERS,
    PERMISSIONS.VIEW_DONATIONS,
    PERMISSIONS.VIEW_POOJAS,
];

export const PERMISSION_GROUPS = [
    {
        name: 'Admin Management',
        permissions: [
            { id: PERMISSIONS.MANAGE_ADMINS, label: 'Manage Admins' },
            { id: PERMISSIONS.VIEW_ADMINS, label: 'View Admins' },
        ]
    },
    {
        name: 'Temple Management',
        permissions: [
            { id: PERMISSIONS.MANAGE_TEMPLES, label: 'Manage Temples' },
            { id: PERMISSIONS.VIEW_TEMPLES, label: 'View Temples' },
            { id: PERMISSIONS.APPROVE_TEMPLES, label: 'Approve Registrations' },
        ]
    },
    {
        name: 'User Management',
        permissions: [
            { id: PERMISSIONS.MANAGE_USERS, label: 'Manage Users' },
            { id: PERMISSIONS.VIEW_USERS, label: 'View Users' },
        ]
    },
    {
        name: 'Donations',
        permissions: [
            { id: PERMISSIONS.MANAGE_DONATIONS, label: 'Manage Donations' },
            { id: PERMISSIONS.VIEW_DONATIONS, label: 'View Donations' },
        ]
    },
    {
        name: 'Others',
        permissions: [
            { id: PERMISSIONS.MANAGE_POOJAS, label: 'Manage Poojas' },
            { id: PERMISSIONS.VIEW_POOJAS, label: 'View Poojas' },
            { id: PERMISSIONS.MANAGE_REPORTS, label: 'View Reports' },
            { id: PERMISSIONS.MANAGE_SETTINGS, label: 'Manage Settings' },
        ]
    }
];

interface PermissionSelectorProps {
    selectedPermissions: string[];
    onChange: (permissions: string[]) => void;
    disabled?: boolean;
}

export const PermissionSelector: React.FC<PermissionSelectorProps> = ({
    selectedPermissions,
    onChange,
    disabled = false
}) => {
    const handleToggle = (permissionId: string) => {
        if (disabled) return;

        if (selectedPermissions.includes(permissionId)) {
            onChange(selectedPermissions.filter(id => id !== permissionId));
        } else {
            onChange([...selectedPermissions, permissionId]);
        }
    };

    const handleGroupToggle = (permissions: string[]) => {
        if (disabled) return;

        const allSelected = permissions.every(p => selectedPermissions.includes(p));

        if (allSelected) {
            // Deselect all in group
            onChange(selectedPermissions.filter(id => !permissions.includes(id)));
        } else {
            // Select all in group
            const newPermissions = [...selectedPermissions];
            permissions.forEach(p => {
                if (!newPermissions.includes(p)) {
                    newPermissions.push(p);
                }
            });
            onChange(newPermissions);
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center gap-2 mb-4">
                <Shield className="w-5 h-5 text-indigo-600" />
                <h3 className="font-medium text-gray-900">Granular Permissions</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {PERMISSION_GROUPS.map((group) => {
                    const groupPermissionIds = group.permissions.map(p => p.id);
                    const allGroupSelected = groupPermissionIds.every(p => selectedPermissions.includes(p));

                    return (
                        <div key={group.name} className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                            <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-200">
                                <span className="font-medium text-sm text-gray-700">{group.name}</span>
                                <button
                                    type="button"
                                    onClick={() => handleGroupToggle(groupPermissionIds)}
                                    className="text-xs text-indigo-600 hover:text-indigo-800"
                                    disabled={disabled}
                                >
                                    {allGroupSelected ? 'Deselect All' : 'Select All'}
                                </button>
                            </div>

                            <div className="space-y-2">
                                {group.permissions.map((permission) => (
                                    <label key={permission.id} className="flex items-center gap-3 cursor-pointer group">
                                        <div className="relative flex items-center">
                                            <input
                                                type="checkbox"
                                                className="peer h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                                                checked={selectedPermissions.includes(permission.id)}
                                                onChange={() => handleToggle(permission.id)}
                                                disabled={disabled}
                                            />
                                        </div>
                                        <span className={`text-sm ${selectedPermissions.includes(permission.id) ? 'text-gray-900 font-medium' : 'text-gray-600'}`}>
                                            {permission.label}
                                        </span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default PermissionSelector;
